import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { MarqueeBreadcrumbs } from '@/components/commons/marquee-breadcrumbs'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import { PlusIcon, User02Icon } from '@/components/ui/icons'
import { CreatorOnlyNotice } from '@/features/marquee/components/creator-only-notice'
import { MarqueeTableCard } from '@/features/marquee/components/marquee-table-card'
import { SearchInput } from '@/features/marquee/components/search-input'
import {
	type TeamFilterOption,
	TeamFilterSelect
} from '@/features/marquee/components/team-filter-select'
import {
	type TeamMemberConfirmAction,
	TeamMemberConfirmDialog
} from '@/features/marquee/components/team-member-confirm-dialog'
import {
	TeamMemberDialog,
	type TeamMemberDialogTarget
} from '@/features/marquee/components/team-member-dialog'
import { TeamTable } from '@/features/marquee/components/team-table'
import {
	operatorQueryOptions,
	rosterQueryOptions
} from '@/features/marquee/query-options'
import { MARQUEE_TEAM_STATUS_OPTIONS } from '@/features/marquee/types'
import {
	isMarketManagerAllowed,
	type TeamFilters,
	type TeamMarketManagerFilter,
	type TeamRowAction,
	teamRowActions,
	teamRowMatchesFilters
} from '@/features/marquee/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { routes } from '@/routes'
import { type MarqueeManagedOperator, sdkSchemas } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const ROLE_FILTER_OPTIONS = [
	{ id: 'all', label: 'marquee:team_filter_all_roles' },
	{ id: 'creator', label: 'marquee:role_creator' },
	{ id: 'facilitator', label: 'marquee:role_facilitator' },
	// Filterable though never assignable: admin rows are on the roster.
	{ id: 'admin', label: 'marquee:role_admin' }
] as const satisfies readonly TeamFilterOption<TeamFilters['role']>[]

const STATUS_FILTER_OPTIONS = [
	{ id: 'all', label: 'marquee:team_filter_all_statuses' },
	{ id: 'active', label: 'marquee:team_status_active' },
	{ id: 'invited', label: 'marquee:team_status_invited' },
	{ id: 'invite_expired', label: 'marquee:team_status_invite_expired' },
	{ id: 'deactivated', label: 'marquee:team_status_deactivated' }
] as const satisfies readonly TeamFilterOption<TeamFilters['status']>[]

const MARKET_MANAGER_FILTER_OPTIONS = [
	{ id: 'all', label: 'marquee:team_filter_all_members' },
	{ id: 'yes', label: 'marquee:team_filter_market_managers' },
	{ id: 'no', label: 'marquee:team_filter_not_market_managers' }
] as const satisfies readonly TeamFilterOption<TeamMarketManagerFilter>[]

const MarqueeTeamSearchParamsSchema = z.object({
	search: z.string().optional(),
	// Enums from the SDK, so a new role or status filters on regenerate.
	role: z
		.enum(['all', ...sdkSchemas.zMarqueeOperatorRole.options])
		.catch('all'),
	status: z.enum(['all', ...MARQUEE_TEAM_STATUS_OPTIONS]).catch('all'),
	marketManager: z.enum(['all', 'yes', 'no']).catch('all')
})

/**
 * The one Team dialog open at a time: the add / edit form, or a confirmation.
 * Held here rather than in the row menu, so the edit form's Deactivate can
 * hand over to the very confirmation the menu opens.
 */
type TeamDialog =
	| { kind: 'form'; target: TeamMemberDialogTarget }
	| {
			kind: 'confirm'
			action: TeamMemberConfirmAction
			member: MarqueeManagedOperator
	  }

function PageShell({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-1 flex-col'>
			<title>{t('marquee:page_title_team')}</title>
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<MarqueeBreadcrumbs />
			</div>
			{children}
		</div>
	)
}

/**
 * Who has access to Marquee: the operator roster, with invite, edit,
 * deactivate / reactivate and resend-invite.
 *
 * Admin work throughout (OPSSTD-9962): the roster decides who may work every
 * event in every market, so `listOperators` is asked before anything about it
 * is fetched, and anyone without it arriving by URL gets the notice and sends
 * no request. The account-menu entry is hidden alongside.
 *
 * The roster comes down whole and every filter runs here, on the one cached
 * list (see `rosterQueryOptions`). Filters live in the URL so a filtered
 * view survives a reload and can be shared.
 *
 * An empty roster is its own state, not a filter that matched nothing: no
 * search or filters to offer, and the add button reads as the first step.
 */
function MarqueeTeam() {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MarqueeTeamSearchParamsSchema
	)
	const [dialog, setDialog] = useState<TeamDialog | null>(null)

	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowedToView = isMarketManagerAllowed(operator, 'listOperators')
	const showAddButton = isMarketManagerAllowed(operator, 'createOperator')

	const { data, isLoading, isError } = useQuery({
		...rosterQueryOptions(),
		enabled: isAllowedToView
	})

	const members = useMemo(() => {
		const filters: TeamFilters = {
			query: searchParams.search ?? '',
			role: searchParams.role,
			status: searchParams.status,
			marketManager: searchParams.marketManager
		}
		return (data?.items ?? []).filter((member) =>
			teamRowMatchesFilters(member, filters)
		)
	}, [
		data,
		searchParams.search,
		searchParams.role,
		searchParams.status,
		searchParams.marketManager
	])

	const isRosterEmpty = !(isLoading || isError) && data?.items.length === 0

	// Stable so the table's column memo is not rebuilt on every render.
	const onAction = useCallback(
		(action: TeamRowAction, member: MarqueeManagedOperator) =>
			setDialog(
				action === 'edit'
					? { kind: 'form', target: { mode: 'edit', member } }
					: { kind: 'confirm', action, member }
			),
		[]
	)

	const closeDialog = () => setDialog(null)
	const editedMember =
		dialog?.kind === 'form' && dialog.target.mode === 'edit'
			? dialog.target.member
			: undefined

	if (!isAllowedToView) {
		return (
			<PageShell>
				<CreatorOnlyNotice
					backLabel={t('marquee:creator_only_back_to_events')}
					backTo={routes.MARQUEE_EVENTS.handle.fullPath}
					description={t('marquee:team_admin_only_description')}
					title={t('marquee:team_admin_only_title')}
				/>
			</PageShell>
		)
	}

	return (
		<PageShell>
			<div className='flex l:flex-row flex-col l:items-start l:justify-between gap-4 l:px-10 px-4 pt-4 pb-8'>
				<div className='flex min-w-0 flex-col gap-1'>
					<h1 className='font-medium text-2xl text-neutral-900'>
						{t('marquee:team_title')}
					</h1>
					<p className='max-w-3xl text-neutral-500 text-sm/4.5'>
						{t('marquee:team_description')}
					</p>
				</div>

				{showAddButton && (
					<div className='flex shrink-0 flex-wrap items-center gap-3'>
						<Button
							onClick={() =>
								setDialog({
									kind: 'form',
									target: { mode: 'create' }
								})
							}
							type='button'
						>
							<PlusIcon />
							{isRosterEmpty
								? t('marquee:team_add_first_member')
								: t('marquee:team_add_member')}
						</Button>
					</div>
				)}
			</div>

			<div className='flex flex-col gap-6 l:px-10 px-4 pb-10'>
				{isRosterEmpty ? (
					<Empty className='rounded-airbnb-2xl border border-grey-200 py-16'>
						<EmptyHeader className='gap-2'>
							<EmptyMedia
								className='size-19 [&>svg]:size-8'
								variant='icon'
							>
								<User02Icon className='text-neutral-500' />
							</EmptyMedia>
							<EmptyTitle>
								{t('marquee:team_no_members_title')}
							</EmptyTitle>
							<EmptyDescription className='max-w-100'>
								{t('marquee:team_no_members_description')}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<>
						{/* One row that never wraps or scrolls the page: the search
						    gives up width first (down to a floor), then the
						    filters truncate their labels. Below `l` the search
						    takes its own row and the filters share the next. */}
						<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-4'>
							<SearchInput
								className='w-full l:min-w-60 l:max-w-120 l:flex-1'
								clearLabel={t('common:clear_search')}
								onChange={(value) =>
									setSearchParams({
										search: value || undefined
									})
								}
								placeholder={t(
									'marquee:team_search_placeholder'
								)}
								value={searchParams.search ?? ''}
							/>
							<div className='flex min-w-0 items-center gap-3 *:min-w-0 *:shrink'>
								<TeamFilterSelect
									label='marquee:team_col_role'
									onChange={(role) =>
										setSearchParams({
											role:
												role === 'all'
													? undefined
													: role
										})
									}
									options={ROLE_FILTER_OPTIONS}
									value={searchParams.role}
								/>
								<TeamFilterSelect
									label='marquee:team_col_status'
									onChange={(status) =>
										setSearchParams({
											status:
												status === 'all'
													? undefined
													: status
										})
									}
									options={STATUS_FILTER_OPTIONS}
									value={searchParams.status}
								/>
								<TeamFilterSelect
									label='marquee:team_col_market_manager'
									onChange={(marketManager) =>
										setSearchParams({
											marketManager:
												marketManager === 'all'
													? undefined
													: marketManager
										})
									}
									options={MARKET_MANAGER_FILTER_OPTIONS}
									value={searchParams.marketManager}
								/>
							</div>
						</div>

						<MarqueeTableCard>
							<TeamTable
								data={members}
								isError={isError}
								isLoading={isLoading}
								onAction={onAction}
							/>
						</MarqueeTableCard>
					</>
				)}
			</div>

			{/* Keyed on the row so each open starts from its own values. */}
			{dialog?.kind === 'form' && (
				<TeamMemberDialog
					key={
						dialog.target.mode === 'edit'
							? dialog.target.member.id
							: 'create'
					}
					onClose={closeDialog}
					onDeactivate={
						editedMember &&
						teamRowActions(editedMember, operator).includes(
							'deactivate'
						)
							? () => onAction('deactivate', editedMember)
							: undefined
					}
					target={dialog.target}
				/>
			)}
			{dialog?.kind === 'confirm' && (
				<TeamMemberConfirmDialog
					action={dialog.action}
					key={`${dialog.action}:${dialog.member.id}`}
					member={dialog.member}
					onClose={closeDialog}
				/>
			)}
		</PageShell>
	)
}

export { MarqueeTeam }
